/**
 * Utilitaires pour la gestion du clavier virtuel sur mobile
 * Gère le repositionnement du widget quand le clavier virtuel s'ouvre
 */

export class VirtualKeyboardHandler {
  constructor(widget) {
    this.widget = widget;
    this.originalViewportHeight = window.innerHeight;
    this.isKeyboardOpen = false;
    this.keyboardHeight = 0;
    this.previousPositioning = null;

    this.init();
  }

  init() {
    // Détecter l'ouverture/fermeture du clavier virtuel
    this.setupViewportChangeDetection();

    // Gérer les événements de focus/blur sur les inputs
    this.setupInputEventHandlers();

    // Gérer le changement d'orientation
    this.setupOrientationChangeHandler();
  }

  setupViewportChangeDetection() {
    let resizeTimeout;

    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.handleViewportChange();
      }, 100);
    });

    // Détecter les changements de hauteur de viewport (indicateur clavier virtuel)
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        this.originalViewportHeight = window.innerHeight;
        this.handleViewportChange();
      }, 500);
    });
  }

  setupInputEventHandlers() {
    if (!this.widget.elements.input) return;

    this.widget.elements.input.addEventListener('focus', () => {
      setTimeout(() => {
        this.handleInputFocus();
      }, 300); // Délai pour laisser le clavier s'ouvrir
    });

    this.widget.elements.input.addEventListener('blur', () => {
      setTimeout(() => {
        this.handleInputBlur();
      }, 100);
    });
  }

  setupOrientationChangeHandler() {
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        if (this.isKeyboardOpen) {
          this.adjustForKeyboard();
        }
      }, 600);
    });
  }

  handleViewportChange() {
    const currentHeight = window.innerHeight;
    const heightDifference = this.originalViewportHeight - currentHeight;

    // Seuil pour détecter l'ouverture du clavier (ajusté selon l'appareil)
    const isSmallDevice = window.innerWidth <= 480;
    const keyboardThreshold = isSmallDevice ? 120 : 150;

    if (heightDifference > keyboardThreshold && !this.isKeyboardOpen) {
      // Clavier ouvert
      this.isKeyboardOpen = true;
      this.keyboardHeight = heightDifference;
      this.adjustForKeyboard();
    } else if (heightDifference <= keyboardThreshold && this.isKeyboardOpen) {
      // Clavier fermé
      this.isKeyboardOpen = false;
      this.keyboardHeight = 0;
      this.restoreOriginalPosition();
    } else if (this.isKeyboardOpen && heightDifference > keyboardThreshold) {
      // Clavier toujours ouvert, mais hauteur changée (rotation par exemple)
      this.keyboardHeight = heightDifference;
      this.adjustForKeyboard();
    }
  }

  handleInputFocus() {
    // Si on est sur mobile et que l'input a le focus
    if (
      this.isMobile() &&
      this.widget.elements.input === document.activeElement
    ) {
      // Attendre que le clavier s'ouvre complètement
      setTimeout(() => {
        this.handleViewportChange();

        // S'assurer que l'input reste visible
        this.scrollInputIntoView();
      }, 300);
    }
  }

  handleInputBlur() {
    // Quand l'input perd le focus, on pourrait restaurer la position
    if (this.isMobile()) {
      setTimeout(() => {
        this.handleViewportChange();
      }, 100);
    }
  }

  adjustForKeyboard() {
    if (!this.widget.elements.modal || !this.isKeyboardOpen) return;

    const modal = this.widget.elements.modal;
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
      availableHeight: window.innerHeight,
    };

    // Sauvegarder la position originale si pas déjà fait
    if (!this.previousPositioning) {
      const computedStyle = window.getComputedStyle(modal);
      this.previousPositioning = {
        bottom: computedStyle.bottom,
        top: computedStyle.top,
        height: computedStyle.height,
        maxHeight: computedStyle.maxHeight,
        transform: computedStyle.transform,
      };
    }

    // Calculer la nouvelle hauteur pour le modal avec plus de précision
    const safeMargin = 80; // Marge de sécurité augmentée pour mobile
    const minModalHeight = 200; // Hauteur minimum réduite pour mobile

    // Hauteur disponible = hauteur actuelle de l'écran - marge de sécurité
    const availableHeight = viewport.availableHeight - safeMargin;

    // Calculer la hauteur optimale pour le modal (réduite pour mobile)
    const optimalHeight = Math.max(
      minModalHeight,
      Math.min(availableHeight, 350) // Max 350px (réduit de 500px) pour mobile
    ); // Repositionner le modal
    const adjustmentClass = 'ringado-keyboard-adjustment';

    if (!document.getElementById('ringado-keyboard-styles')) {
      this.injectKeyboardStyles();
    }

    modal.classList.add(adjustmentClass);

    // Appliquer des styles dynamiques pour ce cas spécifique
    modal.style.setProperty('--keyboard-height', `${this.keyboardHeight}px`);
    modal.style.setProperty('--available-height', `${availableHeight}px`);
    modal.style.setProperty('--modal-height', `${optimalHeight}px`);
    modal.style.setProperty('--safe-margin', `${safeMargin}px`);

    // S'assurer que l'input reste visible
    setTimeout(() => {
      this.scrollInputIntoView();
    }, 100);

    console.log('🎯 Widget adjusted for virtual keyboard:', {
      keyboardHeight: this.keyboardHeight,
      availableHeight: availableHeight,
      modalHeight: optimalHeight,
      viewportHeight: viewport.height,
    });
  }

  restoreOriginalPosition() {
    if (!this.widget.elements.modal) return;

    const modal = this.widget.elements.modal;
    const adjustmentClass = 'ringado-keyboard-adjustment';

    modal.classList.remove(adjustmentClass);

    // Restaurer les styles originaux
    if (this.previousPositioning) {
      modal.style.removeProperty('--keyboard-height');
      modal.style.removeProperty('--available-height');
      modal.style.removeProperty('--modal-height');
      modal.style.removeProperty('--safe-margin');

      // Reset des propriétés de positionnement si nécessaire
      modal.style.removeProperty('bottom');
      modal.style.removeProperty('height');
      modal.style.removeProperty('max-height');
    }

    console.log('↩️ Widget position restored');
  }

  scrollInputIntoView() {
    if (!this.widget.elements.input || !this.isKeyboardOpen) return;

    // Délai pour permettre au DOM de se mettre à jour
    setTimeout(() => {
      const input = this.widget.elements.input;
      const modal = this.widget.elements.modal;
      const messagesContainer = modal.querySelector(
        '.ringado-messages-container'
      );

      // Scroll vers le bas du container de messages pour que l'input soit visible
      if (messagesContainer) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }

      // Vérifier si l'input est visible dans le viewport
      const inputRect = input.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const modalRect = modal.getBoundingClientRect();

      // Si l'input n'est pas complètement visible
      if (
        inputRect.bottom > viewportHeight - 20 ||
        inputRect.top < modalRect.top
      ) {
        // Utiliser scrollIntoView comme solution de secours
        input.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    }, 150);
  }

  injectKeyboardStyles() {
    const styles = `
      /* Styles pour l'ajustement du clavier virtuel */
      .ringado-keyboard-adjustment {
        /* Repositionner le modal quand le clavier est ouvert */
        bottom: var(--safe-margin, 60px) !important;
        height: var(--modal-height, 300px) !important;
        max-height: var(--modal-height, 300px) !important;
        transition: all 0.3s ease !important;
        /* Assurer que le modal reste dans les limites de l'écran */
        top: auto !important;
      }

      /* Container de messages avec scroll optimisé pour le clavier */
      .ringado-keyboard-adjustment .ringado-messages-container {
        /* Maximiser l'espace de scroll disponible */
        flex: 1;
        overflow-y: auto;
        max-height: calc(var(--modal-height, 300px) - 120px);
        padding: 8px 12px !important;
      }

      /* Zone d'input compacte pour économiser l'espace */
      .ringado-keyboard-adjustment .ringado-input-area {
        padding: 8px 12px !important;
        min-height: auto !important;
        flex-shrink: 0;
      }

      /* Header compact */
      .ringado-keyboard-adjustment .ringado-chat-header {
        padding: 8px 12px !important;
        min-height: 48px !important;
        flex-shrink: 0;
      }

      /* Footer compact */
      .ringado-keyboard-adjustment .ringado-chat-footer {
        padding: 6px 12px !important;
        font-size: 11px !important;
        flex-shrink: 0;
      }

      /* Optimisations spécifiques pour iOS */
      @supports (-webkit-touch-callout: none) {
        .ringado-keyboard-adjustment {
          /* Sur iOS, ajuster davantage la position */
          bottom: calc(var(--safe-margin, 60px) + 8px) !important;
        }
        
        .ringado-keyboard-adjustment .ringado-input {
          /* Éviter le zoom automatique sur iOS */
          font-size: 16px !important;
          transform: none !important;
        }
      }

      /* Mode paysage avec clavier - optimisation importante */
      @media (orientation: landscape) and (max-height: 500px) {
        .ringado-keyboard-adjustment {
          bottom: calc(var(--safe-margin, 30px)) !important;
          height: min(var(--modal-height, 250px), calc(100vh - 40px)) !important;
          max-height: min(var(--modal-height, 250px), calc(100vh - 40px)) !important;
        }
        
        .ringado-keyboard-adjustment .ringado-messages-container {
          padding: 4px 8px !important;
          max-height: calc(var(--modal-height, 250px) - 100px);
        }
        
        .ringado-keyboard-adjustment .ringado-input-area {
          padding: 4px 8px !important;
        }

        .ringado-keyboard-adjustment .ringado-chat-header {
          padding: 4px 8px !important;
          min-height: 40px !important;
        }
      }

      /* Petits écrans avec clavier - gestion plus aggressive de l'espace */
      @media (max-width: 480px) {
        .ringado-keyboard-adjustment {
          left: 8px !important;
          right: 8px !important;
          bottom: calc(var(--safe-margin, 40px)) !important;
          width: auto !important;
        }

        .ringado-keyboard-adjustment .ringado-messages-container {
          max-height: calc(var(--modal-height, 280px) - 100px);
        }
      }

      /* Très petits écrans - mode ultra compact */
      @media (max-width: 360px) {
        .ringado-keyboard-adjustment {
          left: 4px !important;
          right: 4px !important;
          bottom: calc(var(--safe-margin, 30px)) !important;
        }

        .ringado-keyboard-adjustment .ringado-messages-container {
          padding: 4px 6px !important;
          max-height: calc(var(--modal-height, 260px) - 80px);
        }

        .ringado-keyboard-adjustment .ringado-input-area {
          padding: 4px 6px !important;
        }
      }

      /* Écrans très courts - priorité absolue à l'efficacité de l'espace */
      @media (max-height: 500px) {
        .ringado-keyboard-adjustment {
          bottom: 20px !important;
          height: min(var(--modal-height, 200px), calc(100vh - 30px)) !important;
          max-height: min(var(--modal-height, 200px), calc(100vh - 30px)) !important;
        }

        .ringado-keyboard-adjustment .ringado-messages-container {
          max-height: calc(var(--modal-height, 200px) - 80px);
          padding: 4px !important;
        }

        .ringado-keyboard-adjustment .ringado-chat-header {
          padding: 4px 8px !important;
          min-height: 36px !important;
        }

        .ringado-keyboard-adjustment .ringado-chat-footer {
          padding: 2px 8px !important;
          font-size: 10px !important;
        }
      }
    `;

    const styleElement = document.createElement('style');
    styleElement.id = 'ringado-keyboard-styles';
    styleElement.textContent = styles;
    document.head.appendChild(styleElement);
  }

  isMobile() {
    return window.innerWidth <= 768 || 'ontouchstart' in window;
  }

  // Nettoyer les event listeners lors de la destruction du widget
  destroy() {
    // Les event listeners seront automatiquement supprimés quand le widget est détruit
    // car ils sont attachés aux éléments du widget

    if (this.isKeyboardOpen) {
      this.restoreOriginalPosition();
    }
  }
}
